import {Injectable, OnDestroy} from '@angular/core';
import {Subject} from 'rxjs';
import {TxHistoryMetaErrorInfo, TxHistoryRec, TxHistoryStatus} from '../../dex-tx-history-widget/tx-history-rec';
import {DexTxError} from './dex-tx-error';
import {getViemRevertFallbackMessage} from './revert-error';

interface ExecTask {
    taskPromise: Promise<unknown>;
}

export enum DexTradeBgExecNotificationType {
    CREATED,
    ERROR
}

export interface DexTradeBgExecNotificationCommon {
    type: DexTradeBgExecNotificationType;
    thRec: TxHistoryRec;
}

export interface DexTradeBgExecNotificationError extends DexTradeBgExecNotificationCommon {
    type: DexTradeBgExecNotificationType.ERROR;
    onAction: () => void;
}

export type DexTradeBgExecNotification = DexTradeBgExecNotificationCommon | DexTradeBgExecNotificationError;
/**
 * Waits for order entering in background,
 * allowing the initiator widget ({@link DexLpTradeWidget}) to be early closed.
 * In case of an error during the order entering, that service handles the error.
 */
@Injectable()
export class DexTradeBgExecService implements OnDestroy {

    private readonly notificationSubject$ = new Subject<DexTradeBgExecNotification>();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly notification$ = this.notificationSubject$.asObservable();

    private isDestroyed: boolean = false;

    private readonly execTasks: ExecTask[] = [];

    public ngOnDestroy() {
        this.isDestroyed = true;
        this.execTasks.length = 0;
    }

    public registerTradeBgExec(params: {
        execPromise: Promise<unknown>;
        thRec: TxHistoryRec,
        onLateError: (error: unknown) => void,
        isLateErrorDelegatedToNotification: boolean
    }): void {
        // eslint-disable-next-line prefer-const
        let task: ExecTask;

        const taskPromise = params.execPromise
            .catch((error: unknown) => {
                if (!this.isDestroyed) {
                    this.handleTaskError({
                        error,
                        thRec:       params.thRec,
                        onLateError: params.onLateError,
                        isLateErrorDelegatedToNotification: params.isLateErrorDelegatedToNotification
                    });
                }
            })
            .finally(() => {
                this.removeExecTask(task);
            });

        task = {
            taskPromise
        } satisfies ExecTask;

        this.execTasks.push(task);

        this.notificationSubject$.next({
            type:  DexTradeBgExecNotificationType.CREATED,
            thRec: params.thRec
        });
    }

    private removeExecTask(task: ExecTask): void {
        const taskIndex = this.execTasks.indexOf(task);

        if (taskIndex >= 0) {
            this.execTasks.splice(taskIndex, 1);
        }
    }

    private handleTaskError(params: {
        error: unknown;
        thRec: TxHistoryRec,
        onLateError: (error: unknown) => void;
        isLateErrorDelegatedToNotification: boolean;
    }): void {
        try {
            const thRec = params.thRec;
            thRec.status = TxHistoryStatus.FAILED;

            if (thRec.metadata) {
                thRec.metadata.error = this.getErrorMessageInfo(params.error);
            }

            this.notificationSubject$.next({
                type:     DexTradeBgExecNotificationType.ERROR,
                thRec,
                onAction: params.isLateErrorDelegatedToNotification
                              ? () => params.onLateError(params.error)
                              : undefined
            });

            if (!params.isLateErrorDelegatedToNotification) {
                params.onLateError(params.error);
            }
        } catch (e) {
            console.error(e);
        }
    }

    public getErrorMessageInfo(e: unknown): TxHistoryMetaErrorInfo | undefined {
        let result: TxHistoryMetaErrorInfo | undefined = undefined;

        if (e) {
            const isDexTxError = e instanceof DexTxError;
            const dexTxError: DexTxError = isDexTxError ? e : undefined;

            if (dexTxError?.naming) {
                result = {
                    msg:    dexTxError.naming.title,
                    isL10n: true,
                    key:    dexTxError.naming.key
                };
            } else {
                result = {
                    msg: dexTxError?.details?.name || getViemRevertFallbackMessage(e) || ''
                };
            }
        }

        return result;
    }

}
